import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { UsersThree, Plus, Trash, Info, Calculator, Coins, TrendUp } from "@phosphor-icons/react";

const money = (v) => `€${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString();
const COLORS = ["#FFB020", "#00A86B", "#5B8DEF", "#E86A92", "#9B8AFB", "#4ECDC4", "#F4A259", "#8AC926"];

function InfoDot({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex align-middle ml-1">
      <button type="button" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)} className="text-white/40 hover:text-[#FFB020]" data-testid="info-dot" aria-label="How this is calculated">
        <Info size={14} weight="bold" />
      </button>
      {open && (
        <span className="absolute z-20 left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 rounded-lg bg-[#0A1311] border border-white/15 text-[11px] leading-relaxed text-white/80 shadow-xl">
          {text}
        </span>
      )}
    </span>
  );
}

export default function CapTable() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/captable");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-white/40 text-sm">Loading…</div>;
  const s = data.summary;
  const pie = s.by_holder.map((h, i) => ({ name: h.name, value: h.shares, fill: COLORS[i % COLORS.length] }));

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Cap Table</div>
        <h1 className="font-display text-4xl">Who owns what</h1>
        <p className="text-white/60 text-sm mt-2">Your fully-diluted ownership, share classes and option pool — plus a round modeller so you never have to guess your dilution again.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={UsersThree} label="Shareholders" value={num(s.by_holder.length)} />
        <Kpi icon={Coins} label="Fully-diluted shares" value={num(s.fd_total_shares)} />
        <Kpi icon={TrendUp} label="Total invested" value={money(s.total_invested)} />
        <Kpi icon={Coins} label="Option pool" value={s.fd_total_shares ? `${((s.option_pool_shares / s.fd_total_shares) * 100).toFixed(1)}%` : "0%"} hint={`${num(s.option_pool_shares)} shares`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="ownership-table">
          <div className="eyebrow mb-4">Ownership</div>
          {s.by_holder.length === 0 ? (
            <div className="text-white/40 text-sm">No shareholders yet. Add founders and issue their shares to get started.</div>
          ) : (
            <table className="data"><thead><tr><th>Holder</th><th>Type</th><th>Shares</th><th>Classes</th><th>%</th></tr></thead>
              <tbody>
                {s.by_holder.map((h, i) => (
                  <tr key={h.shareholder_id} data-testid={`holder-row-${h.shareholder_id}`}>
                    <td className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: COLORS[i % COLORS.length] }} />{h.name}</td>
                    <td className="capitalize text-white/60">{h.holder_type}</td>
                    <td className="mono">{num(h.shares)}</td>
                    <td className="text-white/60 text-xs">{Object.keys(h.classes).join(", ")}</td>
                    <td className="mono text-[#FFB020]">{h.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="glass rounded-2xl p-6 flex items-center justify-center" data-testid="ownership-pie">
          {pie.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {pie.map((e) => <Cell key={e.name} fill={e.fill} stroke="#050B0A" strokeWidth={2} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A1311", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }} formatter={(v) => num(v) + " shares"} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-white/30 text-sm">Ownership chart appears here</div>}
        </div>
      </div>

      <ManageHoldings data={data} onChange={load} />
      <RoundModeller hasShares={s.fd_total_shares > 0} />
      <Rounds data={data} onChange={load} />
    </div>
  );
}

function ManageHoldings({ data, onChange }) {
  const [sh, setSh] = useState({ name: "", holder_type: "founder", email: "" });
  const [hd, setHd] = useState({ shareholder_id: "", share_class: "Ordinary", shares: "", invested: "", round: "" });

  const addSh = async (e) => {
    e.preventDefault();
    try { await api.post("/captable/shareholders", sh); setSh({ name: "", holder_type: "founder", email: "" }); onChange(); toast.success("Shareholder added"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const addHd = async (e) => {
    e.preventDefault();
    if (!hd.shareholder_id) return toast.error("Pick a shareholder");
    try { await api.post("/captable/holdings", { ...hd, shares: Number(hd.shares), invested: Number(hd.invested || 0) }); setHd({ shareholder_id: "", share_class: "Ordinary", shares: "", invested: "", round: "" }); onChange(); toast.success("Shares issued"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const delSh = async (id) => { await api.delete(`/captable/shareholders/${id}`); onChange(); };
  const delHd = async (id) => { await api.delete(`/captable/holdings/${id}`); onChange(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass rounded-2xl p-6" data-testid="add-shareholder">
        <div className="eyebrow mb-4">Add shareholder</div>
        <form onSubmit={addSh} className="space-y-3">
          <div><label>Name</label><input required value={sh.name} onChange={(e) => setSh({ ...sh, name: e.target.value })} data-testid="sh-name" placeholder="Jane Founder" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label>Type</label>
              <select value={sh.holder_type} onChange={(e) => setSh({ ...sh, holder_type: e.target.value })} data-testid="sh-type">
                {data.holder_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label>Email (optional)</label><input value={sh.email} onChange={(e) => setSh({ ...sh, email: e.target.value })} data-testid="sh-email" /></div>
          </div>
          <button className="btn-gold w-full justify-center" data-testid="sh-submit"><Plus size={15} /> Add shareholder</button>
        </form>
        {data.shareholders.length > 0 && (
          <div className="mt-4 space-y-1">
            {data.shareholders.map((h) => (
              <div key={h.shareholder_id} className="flex items-center justify-between text-sm py-1 border-b border-white/5">
                <span>{h.name} <span className="text-white/40 text-xs capitalize">· {h.holder_type}</span></span>
                <button onClick={() => delSh(h.shareholder_id)} className="text-white/30 hover:text-[#E63946]" data-testid={`del-sh-${h.shareholder_id}`}><Trash size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6" data-testid="issue-shares">
        <div className="eyebrow mb-4">Issue shares</div>
        <form onSubmit={addHd} className="space-y-3">
          <div><label>Shareholder</label>
            <select value={hd.shareholder_id} onChange={(e) => setHd({ ...hd, shareholder_id: e.target.value })} data-testid="hd-shareholder">
              <option value="">Select…</option>
              {data.shareholders.map((h) => <option key={h.shareholder_id} value={h.shareholder_id}>{h.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label>Share class</label>
              <select value={hd.share_class} onChange={(e) => setHd({ ...hd, share_class: e.target.value })} data-testid="hd-class">
                {data.share_classes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label>Shares</label><input type="number" required value={hd.shares} onChange={(e) => setHd({ ...hd, shares: e.target.value })} data-testid="hd-shares" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label>Invested € (optional)</label><input type="number" value={hd.invested} onChange={(e) => setHd({ ...hd, invested: e.target.value })} data-testid="hd-invested" /></div>
            <div><label>Round (optional)</label><input value={hd.round} onChange={(e) => setHd({ ...hd, round: e.target.value })} data-testid="hd-round" placeholder="Founders / Seed" /></div>
          </div>
          <button className="btn-gold w-full justify-center" data-testid="hd-submit"><Plus size={15} /> Issue shares</button>
        </form>
        {data.holdings.length > 0 && (
          <div className="mt-4 space-y-1 max-h-40 overflow-auto">
            {data.holdings.map((h) => {
              const owner = data.shareholders.find((x) => x.shareholder_id === h.shareholder_id);
              return (
                <div key={h.holding_id} className="flex items-center justify-between text-sm py-1 border-b border-white/5">
                  <span className="text-white/70">{owner?.name || "?"} · {num(h.shares)} {h.share_class}</span>
                  <button onClick={() => delHd(h.holding_id)} className="text-white/30 hover:text-[#E63946]" data-testid={`del-hd-${h.holding_id}`}><Trash size={13} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function RoundModeller({ hasShares }) {
  const [f, setF] = useState({ pre_money: "", investment: "", new_pool_pct: "", round_name: "Seed" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/captable/model-round", {
        pre_money: Number(f.pre_money), investment: Number(f.investment),
        new_pool_pct: Number(f.new_pool_pct || 0), round_name: f.round_name || "New round",
      });
      setRes(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not model round"); }
    finally { setBusy(false); }
  };

  return (
    <div className="glass rounded-2xl p-6 ring-1 ring-[#FFB020]/40" data-testid="round-modeller">
      <div className="flex items-center gap-2 mb-1"><Calculator size={20} weight="duotone" className="text-[#FFB020]" /><div className="font-display text-xl">Round modeller</div></div>
      <p className="text-white/55 text-sm mb-5">See exactly what a raise does to your ownership before you sign a term sheet. Every figure shows how it was calculated.</p>
      {!hasShares && <div className="text-[#FFB020] text-sm mb-4">Add your existing shares (founders) above first — the modeller needs a starting point.</div>}
      <form onSubmit={run} className="grid md:grid-cols-5 gap-3 items-end">
        <div><label>Round name</label><input value={f.round_name} onChange={(e) => setF({ ...f, round_name: e.target.value })} data-testid="model-name" /></div>
        <div><label>Pre-money € <InfoDot text="The agreed value of your company BEFORE the new money goes in." /></label><input type="number" required value={f.pre_money} onChange={(e) => setF({ ...f, pre_money: e.target.value })} data-testid="model-pre" placeholder="2000000" /></div>
        <div><label>Investment € <InfoDot text="How much new cash the investor is putting in." /></label><input type="number" required value={f.investment} onChange={(e) => setF({ ...f, investment: e.target.value })} data-testid="model-investment" placeholder="500000" /></div>
        <div><label>New pool % <InfoDot text="Optional new option pool for hires, created before the money goes in (so founders absorb it). Leave 0 if none." /></label><input type="number" value={f.new_pool_pct} onChange={(e) => setF({ ...f, new_pool_pct: e.target.value })} data-testid="model-pool" placeholder="0" /></div>
        <button disabled={busy || !hasShares} className="btn-gold justify-center" data-testid="model-run">Model it</button>
      </form>

      {res && (
        <div className="mt-6" data-testid="model-results">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <ResultStat label="Post-money" value={money(res.post_money)} explain={res.explain.post_money} />
            <ResultStat label="Investor stake" value={`${res.investor_pct}%`} explain={res.explain.investor_pct} />
            <ResultStat label="Price / share" value={`€${res.price_per_share}`} explain={res.explain.price_per_share} />
            <ResultStat label="Shares after" value={num(res.total_shares_after)} explain={res.explain.total_shares_after} />
          </div>
          <table className="data"><thead><tr><th>Holder</th><th>Before</th><th>After</th><th>Dilution <InfoDot text={res.explain.dilution} /></th></tr></thead>
            <tbody>
              {res.rows.map((r, i) => (
                <tr key={`${r.name}-${i}`} data-testid={`model-row-${i}`} className={r.is_new ? "text-[#00A86B]" : ""}>
                  <td>{r.name}</td>
                  <td className="mono text-white/50">{r.before_pct}%</td>
                  <td className="mono text-[#FFB020]">{r.after_pct}%</td>
                  <td className="mono">{r.is_new ? "—" : `−${r.dilution_pct}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {Number(f.new_pool_pct) > 0 && <div className="text-xs text-white/50 mt-3">{res.explain.pool}</div>}
        </div>
      )}
    </div>
  );
}

function ResultStat({ label, value, explain }) {
  return (
    <div className="rounded-xl bg-white/[0.03] p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 flex items-center">{label}<InfoDot text={explain} /></div>
      <div className="mono text-xl mt-1 text-white">{value}</div>
    </div>
  );
}

function Rounds({ data, onChange }) {
  const [f, setF] = useState({ name: "", round_type: "Priced", date: "", amount_raised: "", pre_money: "", notes: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/captable/rounds", { ...f, amount_raised: Number(f.amount_raised || 0), pre_money: Number(f.pre_money || 0) }); setF({ name: "", round_type: "Priced", date: "", amount_raised: "", pre_money: "", notes: "" }); onChange(); toast.success("Round recorded"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/captable/rounds/${id}`); onChange(); };
  return (
    <div className="glass rounded-2xl p-6" data-testid="rounds">
      <div className="eyebrow mb-4">Funding rounds</div>
      <form onSubmit={add} className="grid md:grid-cols-6 gap-3 items-end mb-5">
        <div><label>Name</label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="round-name" placeholder="Pre-seed" /></div>
        <div><label>Type</label><select value={f.round_type} onChange={(e) => setF({ ...f, round_type: e.target.value })} data-testid="round-type">{data.round_types.map((t) => <option key={t}>{t}</option>)}</select></div>
        <div><label>Date</label><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="round-date" /></div>
        <div><label>Raised €</label><input type="number" value={f.amount_raised} onChange={(e) => setF({ ...f, amount_raised: e.target.value })} data-testid="round-raised" /></div>
        <div><label>Pre-money €</label><input type="number" value={f.pre_money} onChange={(e) => setF({ ...f, pre_money: e.target.value })} data-testid="round-pre" /></div>
        <button className="btn-gold justify-center" data-testid="round-submit">Record</button>
      </form>
      {data.rounds.length === 0 ? <div className="text-white/40 text-sm">No rounds recorded yet.</div> : (
        <table className="data"><thead><tr><th>Round</th><th>Type</th><th>Date</th><th>Raised</th><th>Pre</th><th>Post</th><th></th></tr></thead>
          <tbody>
            {data.rounds.map((r) => (
              <tr key={r.round_id} data-testid={`round-${r.round_id}`}>
                <td>{r.name}</td><td className="text-white/60">{r.round_type}</td><td className="mono text-white/60">{r.date || "—"}</td>
                <td className="mono">{money(r.amount_raised)}</td><td className="mono text-white/60">{money(r.pre_money)}</td><td className="mono text-white/60">{money(r.post_money)}</td>
                <td className="text-right"><button onClick={() => del(r.round_id)} className="text-white/30 hover:text-[#E63946]" data-testid={`del-round-${r.round_id}`}><Trash size={13} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint }) {
  return (
    <div className="glass rounded-xl p-5" data-testid={`captable-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-white/50"><Icon size={16} /><span className="text-[10px] uppercase tracking-[0.2em]">{label}</span></div>
      <div className="mono text-2xl mt-2">{value}</div>
      {hint ? <div className="text-[11px] text-white/45 mt-1">{hint}</div> : null}
    </div>
  );
}
